// src/components/ProtectedRoute.tsx
import { useEffect, useState } from 'react';
import { Navigate, useLocation, Outlet } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { isPricingFreePath, useAuth } from '../context/AuthContext';
import { getPokerStudyAccessContext } from '../modules/pokerStudy/services/pokerStudyService';
import type { PokerStudyAccessContext } from '../modules/pokerStudy/types/pokerStudy';

const ProtectedRoute = () => {
  const {
    user, isLoading, industrialCostsRole, pricingRole, hasResolvedPricingAccess,
  } = useAuth();
  const location = useLocation();

  const userId = user?.id ?? null;
  // PRC-026: solo se pregunta por Poker a quien puede ser «solo Poker»: sin
  // cuenta de Pricing, o fuera de Pricing (donde no se carga su contexto).
  const pricingFreePath = isPricingFreePath(location.pathname);
  const shouldCheckPoker = pricingFreePath || (hasResolvedPricingAccess && !pricingRole);

  const [pokerAccessContext, setPokerAccessContext] =
    useState<PokerStudyAccessContext | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function loadPokerAccessContext() {
      if (!userId) {
        setPokerAccessContext(null);
        return;
      }

      try {
        const context = await getPokerStudyAccessContext();

        if (!isMounted) {
          return;
        }

        setPokerAccessContext(context);
      } catch (error) {
        console.error('[ProtectedRoute] Poker access context error:', error);

        if (!isMounted) {
          return;
        }

        // Importante:
        // Si falla la comprobación Poker, NO bloqueamos Pricing.
        // Solo usamos este contexto para redirigir cuando sabemos con certeza
        // que el usuario es poker_only.
        setPokerAccessContext(null);
      }
    }

    // Nunca se reutiliza el resultado de otro usuario o de otra situación.
    setPokerAccessContext(null);
    if (!isLoading && shouldCheckPoker) {
      void loadPokerAccessContext();
    }

    return () => {
      isMounted = false;
    };
  }, [isLoading, userId, shouldCheckPoker]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (industrialCostsRole === 'factory_operator') {
    return <Navigate to="/factory/operator" replace />;
  }

  if (industrialCostsRole === 'factory_manager') {
    return <Navigate to="/industrial-cost" replace />;
  }

  if (
    pokerAccessContext?.has_access &&
    pokerAccessContext.is_active &&
    pokerAccessContext.is_poker_only
  ) {
    return <Navigate to="/poker-study" replace />;
  }

  return <Outlet />;
};

export default ProtectedRoute;